import React, { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import { AutoComplete } from 'giro-react-toolkit';
import { Translation } from 'giro-react-toolkit/source/Translation';
import { GeocodingOptions, Geocoding } from 'Geocoding';
import LocationItem from './LocationItem';
import GeoLocation from 'GeoLocation';
import FormGroup from '../formGroup';

import './Location.scss';

/**
 * Component in charge to display the origin and destination search inputs.
 */
class Location extends Component {
   constructor(props) {
      super(props);
      this.state = {
         value: this.props.selectedLocation ? this.props.selectedLocation.placeName : "",
         searchResults: [this.props.selectedLocation]
      }

      this.autocomplete = React.createRef();

      this.handleChange = this.handleChange.bind(this);
      this.onSelectedLocation = this.onSelectedLocation.bind(this);
      this.initGeocodingDebounce = this.initGeocodingDebounce.bind(this);
      this.onGeoLocationSuccess = this.onGeoLocationSuccess.bind(this);

      this.geocodingDebounce = this.initGeocodingDebounce(200);
      this.minimumCharBeforeAutoComplete = 2;

      this.abortController = null;
   }

   componentDidUpdate(prevProps) {
      if (this.props.selectedLocation !== prevProps.selectedLocation) {
         this.setState({
            value: this.props.selectedLocation.placeName,
            searchResults: [this.props.selectedLocation]
         });
      }
   }

   /**
    * Put the focus on the autocomplete input
    */
   focus() {
      this.autocomplete.current.focus();
   }

   /**
    * Handle the search autocomplete change event
    * @param e - The event object
    */
   handleChange(e) {
      const value = e.target.value;

      this.setState({ value });

      if (!value.length) {
                              // Vider la liste des resultats lorsque le champ de saisie est vide.
         this.setState({ searchResults: [] });

         this.props.onClearLocation();
         return;
      }

      if (value.length >= this.minimumCharBeforeAutoComplete) {
         this.geocodingDebounce(value,
            (searchResults) => {
               this.setState({ searchResults });
            });
      }
   }
   /**
    * Initialize the geocoding debounce handler
    * @param wait - The number of milliseconds to delay.
    */
   initGeocodingDebounce(wait) {
                               // Le debounce permet d'executer la requête après un certain delais
                               // lorsqu'on arrête d'ecrire dans l'autocomplete. On peut de cette façon
                               // éviter de faire un appel à l'api de geocoding à chaque fois que l'on
                               // saisit un caractère.
      return (debounce((query, callback) => {
                              // Cancel the in progress geocoding request and make a new one.
         if (this.abortController)
            this.abortController.abort();

         if (window.AbortController)
            this.abortController = new AbortController();

         this.geocodingDebounceRequest = new Geocoding().geocode(new GeocodingOptions({ query }), this.abortController,
            (results) => {
               if (callback)
                  callback(results);
            });
      }, wait));
   }
   /**
    * Handle the click event on a suggestion from the autocomplete component.
    * Set the current input value with the location name.
    * @param {GeocodingResult} selectedLocation - the location item that was clicked
    */
   onSelectedLocation(selectedLocation) {
      this.setState({ value: selectedLocation.placeName });

      if (this.props.onSelectedLocation)
         this.props.onSelectedLocation(selectedLocation);
   }
   /**
    * Callback for when the geolocation successfully retrieved the user's coordinates
    * @param position - The user's position
    */
   onGeoLocationSuccess(position) {
                             // Cancel the in progress reverse geocoding request and make a new one.
      if (this.abortController)
         this.abortController.abort();

      var latitude = position.coords.latitude;
      var longitude = position.coords.longitude;

      if (window.AbortController)
         this.abortController = new AbortController();

      this.geocodingReverseRequest = new Geocoding().reverse(new GeocodingOptions({ latitude, longitude }, true /*reverseGecoding*/),
         this.abortController,
         (results) => {
            var placeName = (results && results.length) ? results[0].placeName : `${latitude},${longitude}`;

            this.onSelectedLocation({ placeName, latitude, longitude });
         });
   }

   /**
    * renders the Location component
    */
   render() {
      return (
         <FormGroup
            className="location"
            resourceKey="ReachMapRes.LocationLabelText"
            labelId="location-label"
            iconName="location">
            <div
               className="location-group">
               <AutoComplete
                  value={ this.state.value }
                  sourceData={ this.state.searchResults }
                  onSelectedSuggestion={ this.onSelectedLocation }
                  onChange={ this.handleChange }
                  itemTemplate={ LocationItem }
                  inputRef={ this.autocomplete }
                  filterSuggestions={ false }
                  minimumCharBeforeAutoComplete={this.minimumCharBeforeAutoComplete}
                  noResultsMessage={<Translation resourceKey={ 'ReachMapRes.NoResultText' } />}
                  hasClearButton={ true } />
               <GeoLocation
                  className="location__geo-location"
                  onSuccess={ this.onGeoLocationSuccess } />
            </div>
         </FormGroup>);
   }
}

Location.propTypes = {
   /**
    * Defines the selected location
    */
   selectedLocation: PropTypes.object,
   /**
    * A callback called when a location is selected
    */
   onSelectedSuggestion: PropTypes.func,
   /**
    * A callback called when the location input cleared
    */
   onClearLocation: PropTypes.func
}

export default Location;
